import type { ReactElement, ReactNode } from 'react'
import type { TFunction } from 'i18next'
import { Copy, Eye, Layers, Plus } from 'lucide-react'

const ENABLED_CLASS = 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
const DISABLED_CLASS = 'text-slate-400 dark:text-slate-500 cursor-not-allowed'

interface MenuButtonProps {
  onClick: () => void
  icon: ReactNode
  label: string
  trailing?: ReactNode
  disabled?: boolean
  title?: string
}

export function MenuButton({
  onClick,
  icon,
  label,
  trailing,
  disabled,
  title,
}: MenuButtonProps): ReactElement {
  const stateClass = disabled ? DISABLED_CLASS : ENABLED_CLASS
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`flex w-full items-center gap-2 px-3 py-2 text-sm ${stateClass}`}
    >
      {icon}
      <span>{label}</span>
      {trailing}
    </button>
  )
}

export function MenuDivider(): ReactElement {
  return <div className="my-1 border-t border-slate-200 dark:border-slate-700" />
}

interface ViewAllProps {
  onActivate: () => void
  relationshipCount: number
  t: TFunction<'cases'>
}

export function ViewAllButton({ onActivate, relationshipCount, t }: ViewAllProps): ReactElement {
  return (
    <MenuButton
      onClick={onActivate}
      icon={<Eye className="h-4 w-4 text-slate-400" />}
      label={t('infrastructure.viewAllLinksButton')}
      trailing={
        relationshipCount > 0 ? (
          <span className="ml-auto text-xs text-slate-400">({relationshipCount})</span>
        ) : null
      }
    />
  )
}

interface CreateLinkProps {
  onActivate: () => void
  t: TFunction<'cases'>
}

export function CreateLinkButton({ onActivate, t }: CreateLinkProps): ReactElement {
  return (
    <MenuButton
      onClick={onActivate}
      icon={<Plus className="h-4 w-4 text-slate-400" />}
      label={t('infrastructure.createLink')}
    />
  )
}

interface BulkCreateProps {
  onActivate: () => void
  hasSelection: boolean
  selectionCount: number
  t: TFunction<'cases'>
}

export function BulkCreateButton({
  onActivate,
  hasSelection,
  selectionCount,
  t,
}: BulkCreateProps): ReactElement {
  const title = hasSelection
    ? t('infrastructure.createLinksToSelected', { count: selectionCount })
    : t('infrastructure.selectEndpointsFirst')
  return (
    <MenuButton
      onClick={onActivate}
      icon={<Layers className="h-4 w-4" />}
      label={t('infrastructure.createBulkLink')}
      disabled={!hasSelection}
      title={title}
      trailing={
        hasSelection ? (
          <span className="ml-auto text-xs text-fuchsia-600 dark:text-fuchsia-400">
            ({selectionCount})
          </span>
        ) : null
      }
    />
  )
}

interface CopyLinksProps {
  onActivate: () => void
  t: TFunction<'cases'>
}

export function CopyLinksButton({ onActivate, t }: CopyLinksProps): ReactElement {
  return (
    <MenuButton
      onClick={onActivate}
      icon={<Copy className="h-4 w-4 text-slate-400" />}
      label={t('infrastructure.copyLinksFrom')}
    />
  )
}
